import { Box } from '../primitives/Box.js';
import { cx } from '../primitives/cx.js';

export type LiquidDayVariant = 'past' | 'today' | 'future';

export type LiquidDayProps = {
  /** Share of the day's scheduled habits that were completed, 0–1. */
  fill: number;
  variant?: LiquidDayVariant;
  /** Screen-reader description, e.g. "Monday, 3 of 5 done". */
  label: string;
  className?: string;
};

/**
 * One day of the week strip, filled like a glass.
 *
 * ONE HUE, on purpose. A red/yellow/green ramp calls a 3-of-5 day a failure
 * and collides head-on with red-green colourblindness in a male-skewed
 * audience. A single emerald that gets brighter as it fills says "more" and
 * "less" without ever saying "bad" — and the gold rim marks the only state
 * that deserves a second colour.
 */
export function LiquidDay({ fill, variant = 'past', label, className }: LiquidDayProps) {
  const level = Math.min(1, Math.max(0, fill));
  const full = level >= 1;

  return (
    <Box
      role="img"
      aria-label={label}
      className={cx(
        'relative size-8 overflow-hidden rounded-full border-2 bg-sunken',
        variant === 'future' && 'border-line opacity-45',
        variant === 'past' && !full && 'border-line-strong',
        variant === 'today' && !full && 'border-accent',
        full && 'border-gold shadow-glow',
        className,
      )}
    >
      {level > 0 && (
        <span
          aria-hidden
          className={cx(
            'absolute inset-x-0 bottom-0 bg-accent',
            'transition-[height,opacity] duration-[var(--aj-duration-slow)] ease-standard',
            'motion-reduce:transition-none',
          )}
          style={{
            height: `${level * 100}%`,
            // The meniscus. Purely cosmetic, and the reason this reads as
            // liquid rather than as a progress bar bent into a circle.
            borderRadius: '50% 50% 0 0 / 9px 9px 0 0',
            // Dim at a trickle, bright when nearly there — the ramp that
            // replaces a second colour.
            opacity: 0.55 + 0.45 * level,
          }}
        />
      )}
    </Box>
  );
}
